import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 48. CONCURRENCY / DOUBLE BOOKING
// Le Core doit permettre aux modules de gérer les conflits sur les
// ressources exclusives : rendez-vous, chambres, appartements, villas,
// ressources, équipements…
//
// Deux utilisateurs ne doivent JAMAIS pouvoir réserver simultanément
// la même ressource exclusive. On réserve la ressource via un verrou
// (core_resource_locks) et la vérification finale est faite ici,
// « côté serveur au moment de l'action » (voir aussi l'Edge Function).
// ─────────────────────────────────────────────────────────────

/** Types de ressources exclusives prises en charge. */
export const EXCLUSIVE_RESOURCE_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-check-line' },
  { key: 'room', label: 'Chambre', icon: 'ri-hotel-bed-line' },
  { key: 'accommodation', label: 'Hébergement', icon: 'ri-hotel-line' },
  { key: 'apartment', label: 'Appartement', icon: 'ri-building-4-line' },
  { key: 'villa', label: 'Villa', icon: 'ri-home-4-line' },
  { key: 'equipment', label: 'Équipement', icon: 'ri-tools-line' },
  { key: 'table', label: 'Table', icon: 'ri-restaurant-2-line' },
  { key: 'vehicle', label: 'Véhicule', icon: 'ri-car-line' },
  { key: 'resource', label: 'Ressource', icon: 'ri-box-3-line' },
];

export function resourceTypeMeta(key: string | null | undefined) {
  return EXCLUSIVE_RESOURCE_TYPES.find((r) => r.key === key) || {
    key: key || 'other',
    label: key || 'Ressource',
    icon: 'ri-box-3-line',
  };
}

export interface CoreResourceLock {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  resource_type: string;
  resource_id: string;
  label: string | null;
  holder_user_id: number | null;
  holder_name: string | null;
  start_at: string | null;
  end_at: string | null;
  status: string | null;
  exclusive: boolean | null;
  entity_type: string | null;
  entity_id: string | null;
  created_at: string | null;
}

export interface ReserveResourceInput {
  tenantId: number | null;
  organizationId: number;
  resourceType: string;
  resourceId: string | number;
  label?: string | null;
  holderUserId?: number | null;
  holderName?: string | null;
  startAt: string;
  endAt: string;
  exclusive?: boolean;
  entityType?: string | null;
  entityId?: string | number | null;
}

export interface ReserveResourceResult {
  ok: boolean;
  lockId: number | null;
  conflict: CoreResourceLock | null;
  error?: string;
}

/** Deux plages [aStart,aEnd) et [bStart,bEnd) se chevauchent-elles ? */
export function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  const a1 = new Date(aStart).getTime();
  const a2 = new Date(aEnd).getTime();
  const b1 = new Date(bStart).getTime();
  const b2 = new Date(bEnd).getTime();
  if ([a1, a2, b1, b2].some((n) => Number.isNaN(n))) return false;
  return a1 < b2 && b1 < a2;
}

/**
 * Cherche un verrou actif qui chevaucherait la plage demandée pour la même
 * ressource. Renvoie le conflit s'il existe, sinon null.
 */
export async function findResourceConflict(input: {
  organizationId: number;
  resourceType: string;
  resourceId: string | number;
  startAt: string;
  endAt: string;
}): Promise<CoreResourceLock | null> {
  const { data, error } = await supabase
    .from('core_resource_locks')
    .select('*')
    .eq('organization_id', input.organizationId)
    .eq('resource_type', input.resourceType)
    .eq('resource_id', String(input.resourceId))
    .eq('status', 'active');
  if (error) throw error;
  const locks = (data || []) as CoreResourceLock[];
  return (
    locks.find((l) =>
      l.exclusive !== false && l.start_at && l.end_at
        ? overlaps(input.startAt, input.endAt, l.start_at, l.end_at)
        : false,
    ) || null
  );
}

/**
 * Réserve une ressource exclusive. Vérifie d'abord les conflits puis pose le
 * verrou. Renvoie { ok:false, conflict } si la ressource est déjà prise sur
 * cette plage — c'est la vérification finale côté Core.
 */
export async function reserveResource(input: ReserveResourceInput): Promise<ReserveResourceResult> {
  const conflict = await findResourceConflict({
    organizationId: input.organizationId,
    resourceType: input.resourceType,
    resourceId: input.resourceId,
    startAt: input.startAt,
    endAt: input.endAt,
  });
  if (conflict) {
    return { ok: false, lockId: null, conflict };
  }

  const { data, error } = await supabase
    .from('core_resource_locks')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      resource_type: input.resourceType,
      resource_id: String(input.resourceId),
      label: input.label || null,
      holder_user_id: input.holderUserId ?? null,
      holder_name: input.holderName ?? null,
      start_at: input.startAt,
      end_at: input.endAt,
      status: 'active',
      exclusive: input.exclusive ?? true,
      entity_type: input.entityType || null,
      entity_id: input.entityId != null ? String(input.entityId) : null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return { ok: true, lockId: (data?.id as number) ?? null, conflict: null };
}

export async function releaseResource(lockId: number): Promise<void> {
  const { error } = await supabase
    .from('core_resource_locks')
    .update({ status: 'released' })
    .eq('id', lockId);
  if (error) throw error;
}

export async function listResourceLocks(
  organizationId: number,
  filter?: { resourceType?: string; status?: string },
): Promise<CoreResourceLock[]> {
  let query = supabase
    .from('core_resource_locks')
    .select('*')
    .eq('organization_id', organizationId)
    .order('start_at', { ascending: false })
    .limit(200);
  if (filter?.resourceType) query = query.eq('resource_type', filter.resourceType);
  if (filter?.status) query = query.eq('status', filter.status);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreResourceLock[];
}

export interface ConcurrencyStats {
  activeLocks: number;
  releasedLocks: number;
  byResourceType: Record<string, number>;
}

export async function fetchConcurrencyStats(organizationId: number): Promise<ConcurrencyStats> {
  const { data } = await supabase
    .from('core_resource_locks')
    .select('resource_type, status')
    .eq('organization_id', organizationId);
  const byResourceType: Record<string, number> = {};
  let activeLocks = 0;
  let releasedLocks = 0;
  (data || []).forEach((r) => {
    const row = r as { resource_type: string; status: string | null };
    byResourceType[row.resource_type] = (byResourceType[row.resource_type] || 0) + 1;
    if (row.status === 'active') activeLocks += 1;
    else if (row.status === 'released') releasedLocks += 1;
  });
  return { activeLocks, releasedLocks, byResourceType };
}